// Deals the faces of the die for one match. Both players roll the same wheel.
// A face keeps its spell for the match while WHEEL_REFRESH is off. With it
// on, a fresh spell of the same polarity replaces a face after it is rolled.
// A spell with an earliest turn is not dealt, and not brought in, before it.
// A spell that answers another is never dealt. After a roll, it takes the
// face of the spell it answers. It gives the face back when it is rolled, or
// at the end of a turn on which it has nothing to answer.

import { FIRST_TURN, WHEEL_QUOTA } from '../constants';
import type { Board } from '../engine/types';
import type { Rng } from '../lib/rng';
import { ALL_SPELLS, SPELLS_BY_POLARITY, spellById } from './library';
import type { Spell, SpellId, SpellPolarity } from './types';

/**
 * Deals the faces one polarity at a time. Boardwide goes first so a buff that
 * needs blockers can see whether the rain is on the wheel.
 */
export const dealWheel = (rng: Rng, board: Board): SpellId[] => {
  const faces: SpellId[] = [];
  const order = (Object.entries(WHEEL_QUOTA) as [SpellPolarity, number][]).sort(([a], [b]) =>
    a === 'board' ? -1 : b === 'board' ? 1 : 0,
  );
  for (const [polarity, count] of order) {
    const pool = rng
      .shuffle(SPELLS_BY_POLARITY[polarity])
      .filter(
        (spell) =>
          spell.answers === undefined &&
          spell.earliestTurn <= FIRST_TURN &&
          spell.canDeal(faces, board),
      );
    faces.push(...pool.slice(0, count).map((spell) => spell.id));
  }
  return rng.shuffle(faces);
};

const canEnter = (
  spell: Spell,
  wheel: readonly SpellId[],
  face: number,
  board: Board,
  turn: number,
): boolean => {
  const others = wheel.filter((_, index) => index !== face);
  return !wheel.includes(spell.id) && spell.earliestTurn <= turn && spell.canDeal(others, board);
};

/**
 * The spell that takes a rolled face because one of the two answers the
 * other. Null when the face stays. A spell that answers the face takes it. A
 * face that answers a spell gives the place back to that spell.
 */
export const answerFor = (
  wheel: readonly SpellId[],
  face: number,
  board: Board,
  turn: number,
): SpellId | null => {
  const currentId = wheel[face];
  if (currentId === undefined) return null;
  const current = spellById(currentId);
  const candidate =
    current.answers !== undefined
      ? spellById(current.answers)
      : ALL_SPELLS.find((spell) => spell.answers === current.id);
  return candidate && canEnter(candidate, wheel, face, board, turn) ? candidate.id : null;
};

/**
 * The spell that takes the place of a rolled face when the wheel refreshes.
 * Null when the face stays. A spell that answers the face comes first. A
 * face that answers a spell leaves only for that spell. Any other face gets
 * a spell of its polarity that is not on the wheel.
 */
export const replacementFor = (
  wheel: readonly SpellId[],
  face: number,
  rng: Rng,
  board: Board,
  turn: number,
): SpellId | null => {
  const currentId = wheel[face];
  if (currentId === undefined) return null;
  const current = spellById(currentId);
  const answer = answerFor(wheel, face, board, turn);
  if (answer !== null || current.answers !== undefined) return answer;
  const unused = SPELLS_BY_POLARITY[current.polarity].filter(
    (spell) => spell.answers === undefined && canEnter(spell, wheel, face, board, turn),
  );
  return unused.length === 0 ? null : rng.pick(unused).id;
};

/**
 * A face that answers a spell and has nothing to answer now, with the spell
 * that comes back in its place. Null when no face is spent.
 */
export const spentAnswer = (
  wheel: readonly SpellId[],
  board: Board,
  turn: number,
): { face: number; replacement: SpellId } | null => {
  for (const [face, id] of wheel.entries()) {
    const spell = spellById(id);
    if (spell.answers === undefined) continue;
    const others = wheel.filter((_, index) => index !== face);
    if (spell.canDeal(others, board)) continue;
    const answered = spellById(spell.answers);
    const canReturn =
      !wheel.includes(answered.id) &&
      answered.earliestTurn <= turn &&
      answered.canDeal(others, board);
    if (canReturn) return { face, replacement: answered.id };
  }
  return null;
};
